import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
  MousePointerClick, Globe, Loader2, Sparkles, Check, ArrowRight,
  TrendingDown, Eye, Target, BarChart3, Pencil
} from "lucide-react";
import { ModuleDescription } from "@/components/ModuleDescription";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ClickbaitOptimizer() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [applying, setApplying] = useState(null);
  const [appliedTitles, setAppliedTitles] = useState({});
  const [customTitle, setCustomTitle] = useState({});
  const [minImpressions, setMinImpressions] = useState(30);
  const [maxCtr, setMaxCtr] = useState(3.0);

  const fetchData = useCallback(async () => {
    try {
      const [sitesRes, statsRes] = await Promise.all([
        axios.get(`${API}/sites`),
        axios.get(`${API}/clickbait/stats`),
      ]);
      const active = (sitesRes.data || []).filter(s => s.status === "active");
      active.sort((a, b) => (b.posts_count || 0) - (a.posts_count || 0));
      setSites(active);
      setStats(statsRes.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const analyzeSite = async (siteId) => {
    setSelectedSite(siteId);
    setAnalyzing(true);
    setAnalysis(null);
    setSuggestions({});
    setAppliedTitles({});
    try {
      const res = await axios.get(`${API}/clickbait/analyze/${siteId}?min_impressions=${minImpressions}&max_ctr=${maxCtr}`);
      setAnalysis(res.data);
    } catch {}
    setAnalyzing(false);
  };

  const generateSuggestions = async (page) => {
    setGenerating(page.page_url);
    try {
      const res = await axios.post(`${API}/clickbait/suggest`, {
        current_title: page.current_title || page.meta_title || "",
        keywords: [],
        page_url: page.page_url,
        ctr: page.ctr,
      });
      setSuggestions(prev => ({ ...prev, [page.page_url]: res.data.suggestions || [] }));
    } catch {}
    setGenerating(null);
  };

  const applyTitle = async (postId, newTitle) => {
    setApplying(postId);
    try {
      const res = await axios.post(`${API}/clickbait/apply`, { post_id: postId, new_title: newTitle });
      setAppliedTitles(prev => ({ ...prev, [postId]: res.data }));
      fetchData();
    } catch {}
    setApplying(null);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="clickbait-optimizer-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <MousePointerClick className="w-8 h-8 text-orange-500" /> Optymalizacja tytulow (CTR)
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Przepisz tytuly artykulow o niskim CTR na bardziej klikalne
          </p>
        </div>
      <ModuleDescription title="Jak dziala optymalizacja CTR?">
        <p><strong>CTR Optimizer</strong> analizuje dane z Google Search Console i znajduje artykuly ktore maja duzo wyswietlen ale malo klikniec (niski CTR).</p>
        <p><strong>Jak to dziala:</strong> Wybierz strone z listy. System pokaze artykuly z niskim CTR. Kliknij "Zaproponuj" — AI wygeneruje lepsze tytuly. Wybierz najlepszy lub wpisz wlasny.</p>
        <p><strong>Filtry:</strong> "min. wyswietl." = minimalna liczba wyswietlen (domyslnie 30) | "max CTR%" = maksymalny CTR do optymalizacji (domyslnie 3%).</p>
        <p><strong>Automatyzacja:</strong> SEO Agent robi to automatycznie co 48h uzywajac Grok + GPT-4o. Ten panel sluzy do recznej kontroli.</p>
      </ModuleDescription>

      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-orange-600">{stats.total_optimized}</p>
              <p className="text-xs text-muted-foreground">Tytulow zoptymalizowanych</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.sites?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Stron objętych</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <Input
                  type="number"
                  value={minImpressions}
                  onChange={e => setMinImpressions(parseInt(e.target.value) || 10)}
                  className="h-7 w-16 text-xs text-center"
                  data-testid="min-impressions-input"
                />
                <span className="text-xs text-muted-foreground">min. wyswietl.</span>
                <Input
                  type="number"
                  step="0.5"
                  value={maxCtr}
                  onChange={e => setMaxCtr(parseFloat(e.target.value) || 1)}
                  className="h-7 w-16 text-xs text-center"
                  data-testid="max-ctr-input"
                />
                <span className="text-xs text-muted-foreground">max CTR%</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Filtry analizy</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sites */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-1.5">
              <Globe className="w-4 h-4" /> Strony
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {sites.filter(s => (s.posts_count || 0) >= 3).map(site => (
                <div
                  key={site.id}
                  data-testid={`ctr-site-${site.id}`}
                  className={`flex items-center justify-between px-4 py-2.5 border-b cursor-pointer hover:bg-muted/30 transition-colors ${selectedSite === site.id ? "bg-orange-50 border-l-2 border-l-orange-500" : ""}`}
                  onClick={() => analyzeSite(site.id)}
                >
                  <div>
                    <div className="text-sm font-medium">{site.name}</div>
                    <div className="text-[10px] text-muted-foreground">{site.posts_count || 0} postow</div>
                  </div>
                  <Target className="w-4 h-4 text-muted-foreground" />
                </div>
              ))}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Analysis */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4" /> Strony z niskim CTR
              {analysis && !analyzing && (
                <Badge className="ml-2 text-xs bg-orange-100 text-orange-700">
                  {analysis.low_ctr_pages} stron do optymalizacji
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedSite && !analyzing && (
              <div className="text-center py-12 text-muted-foreground">
                <MousePointerClick className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Wybierz strone aby przeanalizowac CTR artykulow</p>
              </div>
            )}

            {analyzing && (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-orange-500" />
                <p className="text-sm text-muted-foreground">Analizuje CTR z Google Search Console...</p>
              </div>
            )}

            {analysis && !analyzing && (
              <ScrollArea className="h-[440px]">
                {analysis.error ? (
                  <div className="text-center py-8 text-red-500 text-sm">{analysis.error}</div>
                ) : (
                  <div className="space-y-3">
                    {analysis.pages?.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        Brak stron z CTR ponizej {maxCtr}% i min. {minImpressions} wyswietlen
                      </div>
                    )}
                    {analysis.pages?.map(page => {
                      const pageSuggestions = suggestions[page.page_url] || [];
                      const applied = appliedTitles[page.post_id];
                      return (
                        <div key={page.page_url} className="border rounded-lg p-3" data-testid={`ctr-page-${page.post_id || page.page_url}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">
                                {page.current_title || page.page_url.split("/").filter(Boolean).pop()}
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                                <span className="flex items-center gap-0.5">
                                  <Eye className="w-3 h-3" /> {page.impressions} wyswietl.
                                </span>
                                <span className="flex items-center gap-0.5">
                                  <MousePointerClick className="w-3 h-3" /> {page.clicks} klik.
                                </span>
                                <span className={`font-bold ${page.ctr < 1 ? "text-red-600" : page.ctr < 2 ? "text-orange-600" : "text-amber-600"}`}>
                                  CTR: {page.ctr}%
                                </span>
                                <span>Poz: {page.position}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {applied ? (
                                <Badge className="bg-green-100 text-green-700 text-xs gap-0.5">
                                  <Check className="w-3 h-3" /> Zmieniony
                                </Badge>
                              ) : page.post_id ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs gap-1"
                                  onClick={() => generateSuggestions(page)}
                                  disabled={generating === page.page_url}
                                  data-testid={`suggest-btn-${page.post_id}`}
                                >
                                  {generating === page.page_url ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Sparkles className="w-3 h-3 text-orange-500" />
                                  )}
                                  Zaproponuj
                                </Button>
                              ) : (
                                <Badge variant="outline" className="text-[10px]">Brak w DB</Badge>
                              )}
                            </div>
                          </div>

                          {/* AI Suggestions */}
                          {pageSuggestions.length > 0 && !applied && (
                            <div className="mt-2 space-y-1.5">
                              {pageSuggestions.map((title, i) => (
                                <div key={i} className="flex items-center gap-2 bg-orange-50/50 rounded px-2.5 py-1.5">
                                  <Sparkles className="w-3 h-3 text-orange-500 shrink-0" />
                                  <span className="text-xs flex-1">{title}</span>
                                  <Button
                                    size="sm"
                                    className="h-6 text-[10px] gap-0.5 bg-orange-500 hover:bg-orange-600"
                                    onClick={() => applyTitle(page.post_id, title)}
                                    disabled={applying === page.post_id}
                                    data-testid={`apply-title-${i}`}
                                  >
                                    {applying === page.post_id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Check className="w-2.5 h-2.5" />}
                                    Uzyj
                                  </Button>
                                </div>
                              ))}
                              {/* Custom title */}
                              <div className="flex items-center gap-2 bg-muted/30 rounded px-2.5 py-1.5">
                                <Pencil className="w-3 h-3 text-muted-foreground shrink-0" />
                                <Input
                                  placeholder="Wpisz wlasny tytul..."
                                  value={customTitle[page.post_id] || ""}
                                  onChange={e => setCustomTitle(prev => ({ ...prev, [page.post_id]: e.target.value }))}
                                  className="h-6 text-xs flex-1"
                                />
                                <Button
                                  size="sm"
                                  className="h-6 text-[10px] gap-0.5"
                                  onClick={() => applyTitle(page.post_id, customTitle[page.post_id])}
                                  disabled={!customTitle[page.post_id] || applying === page.post_id}
                                >
                                  <Check className="w-2.5 h-2.5" /> Uzyj
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Applied result */}
                          {applied && (
                            <div className="mt-2 text-xs bg-green-50 rounded p-2">
                              <div className="text-muted-foreground line-through">{applied.old_title}</div>
                              <div className="font-medium text-green-700 flex items-center gap-1 mt-0.5">
                                <ArrowRight className="w-3 h-3" /> {applied.new_title}
                              </div>
                              {applied.wp_update && (
                                <div className="text-[10px] mt-0.5">
                                  WP: {applied.wp_update.ok ? <span className="text-green-600">Zaktualizowany</span> : <span className="text-red-500">Blad</span>}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
